import { countryAtom, paymentChosenAtom } from '@/atoms/demoAtoms';
import SignUpModal from '@/components/modal/sign-up-modal';
import { IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED } from '@/features/flags/legacy-flags';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { ONBOARDING_PAYMENT_CHOSEN } from '@/lib/enums';
import { appendPaymentChosenHashToRedirectUrl } from '@/utils/paymentChosenRedirectUrl';
import { HeidiLoader } from '@heidi/design-system/heidi-loader';
import { useTranslations } from '@heidi/runtime/i18n';
import { getCountry } from '@heidi/utils/get-country';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

const DefaultRegisterPage = () => {
  const router = useRouter();
  const [currentCountry, setCountry] = useAtom(countryAtom);
  const paymentChosenFromAtom = useAtomValue(paymentChosenAtom);
  const { t } = useTranslations();
  const token = useAuthToken();
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();

  const paymentChosenForRedirect =
    !!paymentChosenFromAtom && paymentChosenFromAtom !== ONBOARDING_PAYMENT_CHOSEN.FREE
      ? paymentChosenFromAtom
      : null;

  const shouldRedirectToBilling =
    router.isReady &&
    !router.asPath.includes('integration') &&
    isAuthenticated &&
    !!paymentChosenForRedirect;

  // Redirect authenticated users with paymentChosen to billing and open the corresponding upgrade modal
  useEffect(() => {
    if (!router.isReady || isAuthLoading) return;
    if (router.asPath.includes('integration')) return;

    if (isAuthenticated && !!paymentChosenForRedirect) {
      const billingUrl = appendPaymentChosenHashToRedirectUrl(
        '/settings/billing',
        paymentChosenForRedirect
      );
      void router.replace(billingUrl);
      return;
    }

    const country = getCountry();
    if (country && !currentCountry) {
      setCountry(country);
    }
  }, [
    currentCountry,
    isAuthLoading,
    isAuthenticated,
    paymentChosenForRedirect,
    router,
    setCountry,
  ]);

  if (token && (isAuthLoading || shouldRedirectToBilling)) {
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        <HeidiLoader />
      </div>
    );
  }

  return (
    <div className="h-dvh w-dvw bg-surface">
      <SignUpModal
        title={t('signup.title')}
        subtitle={t('signup.subtitle')}
        isOpen
        showClinicianTypeSelect={!IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED()}
        showCountrySelect
        shouldBlurOverlay
        showFirstName
        showLastName
        shouldShowLogin
        trackSignUpFlowStarted
      />
    </div>
  );
};

export default DefaultRegisterPage;
